import { html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { EwcBase } from '../base/ewc-base';
import { FormValidation, emit, a11y } from '../../adapters/webawesome-adapter';

export type InputType = 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time' | 'datetime-local';

// Apply WebAwesome form validation mixin
const EwcInputBase = FormValidation(EwcBase);

@customElement('ewc-input')
export class EwcInput extends EwcInputBase {
  @query('input') input!: HTMLInputElement;
  
  @property({ type: String })
  type: InputType = 'text';

  @property({ type: String }) placeholder = '';
  @property({ type: String }) label = '';
  @property({ type: String }) error = '';
  @property({ type: String }) helper = '';
  @property({ type: String }) pattern = '';
  @property({ type: Number }) minlength?: number;
  @property({ type: Number }) maxlength?: number;
  @property({ type: String }) icon = '';
  @property({ type: Boolean }) clearable = false;
  @property({ type: Boolean }) autofocus = false;
  @property({ type: String }) autocomplete?: string;
  @property({ type: String }) inputmode?: string;
  @property({ type: Boolean }) filled = false;
  @property({ type: String }) prefix = '';
  @property({ type: String }) suffix = '';
  @property({ type: Number }) min?: number;
  @property({ type: Number }) max?: number;
  @property({ type: Number }) step?: number;

  @state()
  private _focused = false;

  @state()
  private _showPassword = false;

  static override get styles() {
    return [
      ...EwcInputBase.styles,
      css`
        :host {
          display: block;
          width: 100%;
        }

        .input-wrapper {
          position: relative;
          width: 100%;
        }

        .label {
          display: block;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--ewc-text-color-default);
        }

        .input-container {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
        }

        input {
          width: 100%;
          padding: var(--ewc-padding-y) var(--ewc-padding-x);
          min-height: var(--ewc-height);
          font-size: var(--ewc-font-size);
          font-family: inherit;
          border: 2px solid var(--ewc-border-color);
          border-radius: var(--ewc-radius);
          background-color: var(--ewc-bg-color);
          color: var(--ewc-text-color-default);
          transition: all 0.2s;
        }

        /* Dynamic padding based on icon/clearable state */
        :host([icon]) input {
          padding-left: 2.5rem;
        }

        :host([clearable]) input,
        input[type="password"] {
          padding-right: 2.5rem;
        }

        input:focus {
          outline: none;
          border-color: var(--ewc-color);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        input:disabled {
          background-color: var(--ewc-bg-tertiary);
          cursor: not-allowed;
        }

        /* Error state */
        :host([error]) input,
        .input:invalid {
          border-color: var(--ewc-color-error, #ef4444);
        }

        :host([error]) input:focus,
        .input:invalid:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        /* Valid state */
        input:valid:not(:placeholder-shown):not(:focus) {
          border-color: var(--ewc-color-success, #10b981);
        }

        /* Filled variant */
        :host([filled]) input {
          background-color: var(--ewc-bg-secondary);
          border-color: transparent;
        }

        :host([filled]) input:focus {
          background-color: var(--ewc-bg-color);
          border-color: var(--ewc-color);
        }

        /* Prefix/Suffix */
        .prefix, .suffix {
          position: absolute;
          display: flex;
          align-items: center;
          color: var(--ewc-text-color-secondary);
          font-size: 0.875rem;
          pointer-events: none;
        }

        .prefix {
          left: 0.75rem;
        }

        .suffix {
          right: 0.75rem;
        }

        input.has-prefix {
          padding-left: calc(var(--prefix-width, 2rem) + 0.75rem);
        }

        input.has-suffix {
          padding-right: calc(var(--suffix-width, 2rem) + 0.75rem);
        }

        /* Icons */
        .icon-left,
        .icon-right {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.5rem;
          height: 100%;
          color: var(--ewc-text-color-secondary);
        }

        .icon-left {
          left: 0;
          pointer-events: none;
        }

        .icon-right {
          right: 0;
        }

        .icon-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 1.5rem;
          height: 1.5rem;
          border: none;
          background: none;
          color: inherit;
          cursor: pointer;
          border-radius: 0.25rem;
          transition: all 0.2s;
        }

        .icon-button:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        :host([theme="dark"]) .icon-button:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        /* Helper text */
        .helper-text,
        .error-text {
          margin-top: 0.25rem;
          font-size: 0.75rem;
          color: var(--ewc-text-color-secondary);
        }

        .error-text {
          color: var(--ewc-color-error, #ef4444);
        }

        /* Floating label */
        .floating-label {
          position: absolute;
          left: var(--ewc-padding-x);
          top: 50%;
          transform: translateY(-50%);
          font-size: var(--ewc-font-size);
          color: var(--ewc-text-color-secondary);
          pointer-events: none;
          transition: all 0.2s;
          background-color: var(--ewc-bg-color);
          padding: 0 0.25rem;
        }

        input:focus ~ .floating-label,
        input:not(:placeholder-shown) ~ .floating-label,
        .input-container.has-value .floating-label {
          top: 0;
          font-size: 0.75rem;
          color: var(--ewc-color);
        }
      `
    ];
  }

  protected firstUpdated() {
    if (this.autofocus) {
      this.input?.focus();
    }
    this._updateAccessibility();
  }

  protected updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has('value')) {
      emit(this, 'ewc-input', { detail: { value: this.value } });
    }

    if (changedProperties.has('disabled') || changedProperties.has('required')) {
      this._updateAccessibility();
    }
  }

  private _updateAccessibility() {
    if (this.input) {
      a11y.setAttributes(this.input, {
        'aria-invalid': this.error || !this.validity.valid,
        'aria-required': this.required,
        'aria-disabled': this.disabled,
        'aria-label': this.label || this.placeholder
      });
    }
  }

  render() {
    const hasPrefix = !!this.prefix;
    const hasSuffix = !!this.suffix;
    const hasClear = this.clearable && this.value && !this.disabled && !this.readonly;

    const inputClasses = {
      'has-prefix': hasPrefix,
      'has-suffix': hasSuffix
    };

    return html`
      <div class="input-wrapper">
        ${this.label && !this._isFloatingLabel() ? 
          html`<label class="label">${this.label}${this.required ? html`<span style="color: var(--ewc-color-error);"> *</span>` : ''}</label>` : ''}
        
        <div class="input-container ${this.value ? 'has-value' : ''}">
          ${this.icon ? html`
            <span class="icon-left">${this.icon}</span>
          ` : ''}
          
          ${hasPrefix ? html`<span class="prefix">${this.prefix}</span>` : ''}
          
          <input
            class=${classMap(inputClasses)}
            type="${this.type === 'password' && this._showPassword ? 'text' : this.type}"
            .value="${this.value}"
            placeholder="${this.placeholder}"
            ?disabled="${this.disabled}"
            ?readonly="${this.readonly}"
            ?required="${this.required}"
            autocomplete=${ifDefined(this.autocomplete)}
            inputmode=${ifDefined(this.inputmode)}
            pattern=${ifDefined(this.pattern)}
            min=${ifDefined(this.min)}
            max=${ifDefined(this.max)}
            step=${ifDefined(this.step)}
            minlength=${ifDefined(this.minlength)}
            maxlength=${ifDefined(this.maxlength)}
            @input="${this._handleInput}"
            @change="${this._handleChange}"
            @focus="${this._handleFocus}"
            @blur="${this._handleBlur}"
            @invalid="${this._handleInvalid}"
          />
          
          ${this._isFloatingLabel() ? 
            html`<label class="floating-label">${this.label}</label>` : ''}
          
          ${hasSuffix ? html`<span class="suffix">${this.suffix}</span>` : ''}
          
          <span class="icon-right">
            ${this.type === 'password' ? html`
              <button
                class="icon-button"
                @click="${this._togglePasswordVisibility}"
                tabindex="-1"
                aria-label="${this._showPassword ? 'Hide password' : 'Show password'}"
              >
                ${this._showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            ` : ''}
            
            ${hasClear ? html`
              <button
                class="icon-button"
                @click="${this._handleClear}"
                tabindex="-1"
                aria-label="Clear input"
              >
                ✕
              </button>
            ` : ''}
          </span>
        </div>
        
        ${this.error || this.validationMessage ? 
          html`<div class="error-text" role="alert">${this.error || this.validationMessage}</div>` : 
          this.helper ? 
            html`<div class="helper-text">${this.helper}</div>` : ''}
      </div>
    `;
  }

  private _isFloatingLabel(): boolean {
    return this.label && !this.placeholder;
  }

  private _handleInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.value = input.value;
    emit(this, 'ewc-input', { detail: { value: this.value } });
  }

  private _handleChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.value = input.value;
    emit(this, 'ewc-change', { detail: { value: this.value } });
    this.reportValidity();
  }

  private _handleFocus() {
    this._focused = true;
    emit(this, 'ewc-focus');
  }

  private _handleBlur() {
    this._focused = false;
    emit(this, 'ewc-blur');
    this.reportValidity();
  }

  private _handleClear() {
    this.value = '';
    emit(this, 'ewc-clear');
    emit(this, 'ewc-input', { detail: { value: '' } });
    emit(this, 'ewc-change', { detail: { value: '' } });
    this.input?.focus();
  }

  private _handleInvalid(e: Event) {
    e.preventDefault();
    this.reportValidity();
  }

  private _togglePasswordVisibility() {
    this._showPassword = !this._showPassword;
  }

  // Public methods
  focus() {
    this.input?.focus();
  }

  blur() {
    this.input?.blur();
  }

  select() {
    this.input?.select();
  }

  setSelectionRange(start: number, end: number, direction?: 'forward' | 'backward' | 'none') {
    this.input?.setSelectionRange(start, end, direction);
  }
}